/** Gedeelde admin UI-helpers: badges, headers, loading, error, formatters. */
import React from "react";

/* STATUS_KLEUR (de oude Tailwind-klassen per status) is vervallen bij de restyling.
   De tonen staan nu in STATUS_TOON verderop, met de kleuren uit het designsysteem.
   Geen enkel bestand gebruikte STATUS_KLEUR nog buiten dit bestand. */

export const STATUS_LABEL = {
  // werknemer
  aangemeld: "Aangemeld",
  uitgenodigd: "Uitgenodigd",
  baseline_voltooid: "Nulmeting voltooid",
  aangemeten: "Aangemeten",
  klaar_om_te_bestellen: "Klaar om te bestellen",
  besteld: "Zolen besteld",
  binnengekomen: "Binnen bij ons",
  geleverd: "Zolen geleverd",
  week4_voltooid: "Zolen-wencontrole voltooid (wk 4)",
  week12_voltooid: "Vervolgmeting voltooid (wk 12)",
  afgerond: "Afgerond",
  gepauzeerd: "Gepauzeerd",
  opgezegd: "Opgezegd",
  geanonymiseerd: "Geanonimiseerd",
  // aanmeetdag
  voorgesteld: "Voorgesteld",
  voltooid: "Voltooid",
  // werkgever
  actief: "Actief",
  beeindigd: "Beëindigd",
  // notificaties
  pending: "In wachtrij",
  sending: "Wordt verzonden",
  verzonden: "Verzonden",
  geopend: "Geopend",
  gefaald: "Mislukt",
  overgeslagen: "Overgeslagen",
  bounced: "Onbestelbaar",
  complained: "Klacht ontvangen",
  // bestellingen
  in_productie: "In productie",
  foutief: "Foutief",
  herbesteld: "Opnieuw besteld",
  open: "Open",
  geannuleerd: "Geannuleerd",
  bevestigd: "Bevestigd",
  afgehandeld: "Afgehandeld",
  // aanmeetdag en uitvoering
  gepland: "Gepland",
  in_uitvoering: "In uitvoering",
  no_show: "Niet verschenen",
  verzet: "Verzet",
};

/* NOTIF_TYPE_LABEL stond hier. Het dekte zeven soorten berichten terwijl de database er
   vijftien kent, en vier van de zeven bestonden niet eens meer. De namen staan nu
   compleet in lib/namen.js (BERICHT_NAMEN); één lijst, met een test die hem sluitend
   houdt. */

export function labelize(value, dict) {
  if (!value) return "—";
  return dict[value] || value;
}

export function StatusBadge({ value, testid, ...rest }) {
  /* Huisstijl: dezelfde statuslabels, maar in de tonen van het designsysteem.
     De kleuren komen uit STATUS_TOON hieronder — rood, oranje en groen staan
     uitsluitend op iets dat die betekenis ook heeft. */
  if (!value) return <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>—</span>;
  const toon = STATUS_TOON[value] || "neutraal";
  const t = TOON_STIJL[toon];
  return (
    <span
      data-testid={testid}
      style={{
        display: "inline-flex", alignItems: "center", gap: "var(--ruimte-2)",
        padding: "2px var(--ruimte-4)", borderRadius: "var(--radius-pil)",
        font: "var(--text-label)", fontSize: "var(--fs-xs)", whiteSpace: "nowrap", ...t,
      }}
      {...rest}
    >
      {STATUS_LABEL[value] || value}
    </span>
  );
}

const TOON_STIJL = {
  neutraal: { background: "var(--neutraal-100)", color: "var(--text-body)" },
  actie: { background: "var(--actie-zacht)", color: "var(--accent)" },
  positief: { background: "var(--positief-zacht)", color: "var(--text-positief)" },
  aandacht: { background: "var(--aandacht-zacht)", color: "var(--text-aandacht)" },
  alarm: { background: "var(--alarm-zacht)", color: "var(--text-alarm)" },
  inkt: { background: "var(--ink)", color: "var(--text-inverse)" },
};

/** Welke toon hoort bij welke status. Eén op één uit het designsysteem (StatusBadge). */
export const STATUS_TOON = {
  aangemeld: "neutraal", uitgenodigd: "aandacht", baseline_voltooid: "actie", gepland: "actie",
  aangemeten: "actie", besteld: "aandacht", verzonden: "actie", geleverd: "positief",
  week4_voltooid: "actie", week12_voltooid: "actie", afgerond: "positief", gepauzeerd: "neutraal",
  opgezegd: "alarm", no_show: "alarm", geanonymiseerd: "neutraal", voorgesteld: "aandacht",
  voltooid: "positief", actief: "positief", beeindigd: "alarm", pending: "neutraal",
  sending: "actie", geopend: "positief", gefaald: "alarm", overgeslagen: "aandacht",
  bounced: "alarm", complained: "alarm", open: "aandacht", geannuleerd: "alarm",
  bevestigd: "positief", afgehandeld: "positief", in_productie: "actie", foutief: "alarm",
  herbesteld: "aandacht", in_uitvoering: "actie", verzet: "aandacht",
};

export function PageHeader({ title, subtitle, actions, testid, size = "h2" }) {
  /* Het ontwerp zet paginakoppen op 24px (--text-h2); alleen het dashboard staat op
     display-formaat. Stond hier eerder vast op --text-h1 (32px), waardoor elk scherm
     zonder eigen maat te groot uitviel. */
  const kop = size === "display" ? "var(--text-display)" : size === "h1" ? "var(--text-h1)" : "var(--text-h2)";
  return (
    <div
      data-testid={testid}
      style={{
        marginBottom: "var(--ruimte-8)", display: "flex", flexWrap: "wrap",
        alignItems: "flex-end", justifyContent: "space-between", gap: "var(--ruimte-6)",
      }}
    >
      <div>
        <h1 style={{ font: kop, letterSpacing: size === "display" ? "var(--ls-display)" : "var(--ls-kop)", color: "var(--text-strong)" }}>{title}</h1>
        {subtitle ? (
          <p style={{ marginTop: "var(--ruimte-3)", fontSize: "var(--fs-body)", color: "var(--text-soft)" }}>{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)" }}>{actions}</div> : null}
    </div>
  );
}

const MELDCEL = {
  padding: "var(--ruimte-9)", textAlign: "center",
  fontSize: "var(--fs-body)", color: "var(--text-soft)",
};

export function LoadingRow({ cols = 5 }) {
  return (
    <tr data-testid="loading-row">
      <td colSpan={cols} style={MELDCEL}>Laden…</td>
    </tr>
  );
}

export function EmptyRow({ cols = 5, label = "Geen resultaten" }) {
  return (
    <tr data-testid="empty-row">
      <td colSpan={cols} style={MELDCEL}>{label}</td>
    </tr>
  );
}

import { formatApiError } from "../../lib/adminApi";

export function ErrorBanner({ error, testid = "error-banner" }) {
  if (!error) return null;
  const msg = formatApiError(error);
  return (
    <div
      data-testid={testid}
      role="alert"
      style={{
        marginBottom: "var(--ruimte-6)", borderRadius: "var(--radius)",
        border: "1px solid var(--alarm-rand)", background: "var(--alarm-zacht)",
        padding: "var(--ruimte-5) var(--ruimte-6)", fontSize: "var(--fs-body)",
        color: "var(--text-alarm)",
      }}
    >
      {String(msg)}
    </div>
  );
}

/**
 * Merkt een adres waar geen mail heen kan.
 *
 * De server meldt met `<veld>_geldig: false` dat een opgeslagen adres de controle niet
 * haalt. Zonder dit merkje ziet een beheerder pas weken later — via een mislukte
 * notificatie — dat er nooit iets is aangekomen, en klikt hij ondertussen op een
 * mailto-link die nergens heen gaat.
 *
 * Gebruik het altijd met `=== false`: een oudere server die het veld niet meestuurt
 * hoort niets te merken.
 */
export function OnbruikbaarMerkje({ testid }) {
  return (
    <span
      data-testid={testid}
      title="Dit adres komt niet door de controle; er kan geen mail heen."
      style={{
        marginLeft: "var(--ruimte-4)", display: "inline-flex", alignItems: "center",
        borderRadius: "var(--radius)", padding: "2px var(--ruimte-4)",
        fontSize: "var(--fs-xs)", fontWeight: "var(--fw-medium)",
        background: "var(--alarm-zacht)", color: "var(--text-alarm)",
      }}
    >
      onbruikbaar adres
    </span>
  );
}

/**
 * Meldt dat de server rijen heeft overgeslagen die niet in het model pasten.
 *
 * Aanleiding (2026-08-19): één rij met een onbruikbaar e-mailadres gaf een 500 op een
 * complete lijst. De server slaat zo'n rij nu over in plaats van om te vallen — maar
 * zonder deze melding is de winst een luide fout ingeruild voor een stille: de rij is
 * weg, de teller zegt niets, en het enige spoor staat in een serverlogboek dat niemand
 * leest. Op de pijn-flags-lijst is dat een klinisch risico: een signaal dat er niet meer
 * bij staat, is niet te onderscheiden van een signaal dat er nooit was.
 *
 * `aantal` mag undefined zijn — een oudere server die het veld niet meestuurt hoort
 * niets te melden.
 */
export function OvergeslagenBanner({ aantal, enkelvoud, meervoud, testid = "overgeslagen-melding" }) {
  if (!aantal || aantal < 1) return null;
  return (
    <div
      data-testid={testid}
      role="alert"
      style={{
        marginBottom: "var(--ruimte-6)", borderRadius: "var(--radius)",
        border: "1px solid var(--aandacht-rand)", background: "var(--aandacht-zacht)",
        padding: "var(--ruimte-5) var(--ruimte-6)", fontSize: "var(--fs-body)",
        color: "var(--text-aandacht)",
      }}
    >
      {aantal === 1
        ? `Eén ${enkelvoud} op deze pagina kon niet worden getoond omdat de gegevens onvolledig zijn.`
        : `${aantal} ${meervoud} op deze pagina konden niet worden getoond omdat hun gegevens onvolledig zijn.`}{" "}
      De reden staat in het serverlogboek. Op andere pagina's kunnen er meer zijn.
    </div>
  );
}

export function PaginationBar({ offset, limit, total, onPrev, onNext }) {
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + limit, total);
  /* Huisstijl: dezelfde knoppen, maar in de vormgeving van het designsysteem.
     Secundaire knop = wit met een dunne rand; uitgeschakeld = halve dekking. */
  const knop = (uit) => ({
    minHeight: 32, padding: "0 var(--ruimte-5)", borderRadius: "var(--radius)",
    border: "1px solid var(--border-subtle)", background: "var(--wit)",
    color: "var(--text-strong)", fontSize: "var(--fs-sm)", fontFamily: "var(--font-tekst)",
    fontWeight: "var(--fw-semi)", boxShadow: "var(--schaduw-1)",
    cursor: uit ? "default" : "pointer", opacity: uit ? 0.5 : 1,
    transition: "var(--overgang-kleur)",
  });
  return (
    <div
      data-testid="pagination"
      style={{
        marginTop: "var(--ruimte-5)", display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: "var(--ruimte-5)",
        fontSize: "var(--fs-xs)", color: "var(--text-soft)",
      }}
    >
      <span>{showingFrom}–{showingTo} van {total}</span>
      <div style={{ display: "flex", gap: "var(--ruimte-4)" }}>
        <button type="button" disabled={offset === 0} onClick={onPrev} data-testid="pagination-prev" style={knop(offset === 0)}>
          Vorige
        </button>
        <button type="button" disabled={showingTo >= total} onClick={onNext} data-testid="pagination-next" style={knop(showingTo >= total)}>
          Volgende
        </button>
      </div>
    </div>
  );
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export function formatDateOnly(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return iso;
  }
}
